import React, { useEffect, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { DataProviderInstance } from '../../../api/DataProvider';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../components/buttons/CustomButton';
import usePlanLock from '../../../hooks/usePlanLock';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import Toast from '../../../components/support/Toast';
import { SUBSCRIPTION_PERMISSIONS } from '../../../consts/SubscriptionFeatures';
import { BUSINESS_PLAN_NAME } from '../../../components/subscription/planLockTexts';
import SourceRow from './SourceRow';
import CreateEditSourcePopup from './CreateEditSourcePopup';
import MarketingListCard from '../shared/MarketingListCard';
import MarketingEmptyState from '../shared/MarketingEmptyState';
import MarketingListSkeleton from '../shared/MarketingListSkeleton';
import shared from '../shared/MarketingShared.module.css';

function Sources() {
  const { GetMarketingChannels } = DataProviderInstance();

  const [loaded, setLoaded] = useState(false);
  const [sources, setSources] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSource, setCurrentSource] = useState(null);
  const [createOrEditPopupVisibility, setCreateOrEditPopupVisibility] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  // Cadeado por plano (Business): as origens continuam visíveis; criar,
  // editar e excluir abrem o modal "Falar com a gente".
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.MARKETING, 'O cadastro de origens', BUSINESS_PLAN_NAME);

  // Plano ainda não conhecido: a tela espera junto com a lista, para o
  // banner do cadeado aparecer com o conteúdo (nunca parecer liberada)
  const { loading: planLoading, planConfig } = useSubscription();
  const planReady = !planLoading || planConfig !== null;

  useEffect(() => {
    setNeedsReload(true);
  }, []);

  useEffect(() => {
    if (needsReload) {
      setLoaded(false);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsReload]);

  const loadData = () => {
    GetMarketingChannels((result) => {
      setSources(result.data);
      setLoaded(true);
      setNeedsReload(false);
    });
  };

  useEffect(() => {
    const filteredSources = sources.filter((source) => {
      return source.name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    setFiltered(filteredSources);
  }, [searchTerm, sources]);

  function showCreateEditSourcePopup(sourceData) {
    setCurrentSource(sourceData);
    setCreateOrEditPopupVisibility(true);
  }

  function onCreateEditSourcePopupHide() {
    setCurrentSource(null);
    setCreateOrEditPopupVisibility(false);
  }

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  function onSourceUpdate(response, isEdit) {
    setNeedsReload(true);
    showToast(isEdit ? 'Origem atualizada' : 'Origem criada');
  }

  function onSourceDeleted() {
    setNeedsReload(true);
    showToast('Origem excluída');
  }

  function onCreateSourceClickHandler() {
    setCurrentSource(null);
    setCreateOrEditPopupVisibility(true);
  }

  // Na recarga depois de salvar, a lista atual fica na tela (sem piscar)
  const firstLoad = (!loaded && sources.length === 0) || !planReady;
  const isEmpty = !firstLoad && loaded && sources.length === 0;

  return (
    <div className='page-content'>
      <div className={shared.page}>
        <div className={shared.pageHeader}>
          <AreaTitle
            Title='Origens'
            Description="De onde vêm os seus leads: Instagram, Google, indicação e outros canais."
            Icon={<HubOutlinedIcon />}
          />
          <CustomButton
            icon={<AddRoundedIcon />}
            variant="contained"
            style="main"
            enabled={planReady}
            label="Nova origem"
            onClick={lock.guard(onCreateSourceClickHandler)}
          />
        </div>
        {lock.banner}
        {lock.modal}

        <MarketingListCard
          title="Origens cadastradas"
          count={firstLoad ? null : sources.length}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showSearch={!firstLoad && !isEmpty}
        >
          {firstLoad && <MarketingListSkeleton />}
          {isEmpty && (
            <MarketingEmptyState
              step="source"
              title="Nenhuma origem cadastrada"
              text="Cadastre os canais por onde as pessoas chegam até você. Depois, cada campanha fica ligada a uma origem."
              actionLabel="Cadastrar primeira origem"
              onAction={lock.guard(onCreateSourceClickHandler)}
            />
          )}
          {!firstLoad && !isEmpty && (
            filtered.length === 0 ? (
              <MarketingEmptyState searching />
            ) : (
              <div className={shared.scroll}>
                <ul className={shared.list}>
                  {filtered.map((sourceData, index) => (
                    <SourceRow
                      key={sourceData.id ?? index}
                      sourceData={sourceData}
                      onEdit={lock.guard(() => showCreateEditSourcePopup(sourceData))}
                      onUpdate={onSourceDeleted}
                      onBlockedAction={lock.locked ? lock.openUpgrade : null}
                    />
                  ))}
                </ul>
              </div>
            )
          )}
        </MarketingListCard>

        {createOrEditPopupVisibility && (
          <CreateEditSourcePopup
            OnHide={onCreateEditSourcePopupHide}
            OnUpdate={onSourceUpdate}
            Data={currentSource}
          />
        )}

        <Toast
          open={toast.open}
          onClose={() => setToast({ ...toast, open: false })}
          message={toast.message}
          severity={toast.severity}
        />
      </div>
    </div>
  );
}

export default Sources;
